import { useCallback, useRef } from "react";

import { useAdvisorDrawerStore } from "../stores/advisor";

/**
 * Ask Sill drawer focus-return fix (UI review, post-launch). Shared by
 * `AdvisorDrawer` and `AdvisorDrawerPreview` — both render the SAME
 * trigger-less `DialogPrimitive.Content` shape driven by
 * `useAdvisorDrawerStore`'s `drawerOpen`, with no `DialogPrimitive.Trigger`
 * anywhere (the drawer opens from a dozen entry points — header pill,
 * sidebar item, mobile tab, `⌘/Ctrl+K`, a contextual CTA — none of which
 * render inside the dialog tree).
 *
 * Radix's own `onCloseAutoFocus` default only knows how to restore focus
 * to a `DialogPrimitive.Trigger` ref, which is permanently null here. On
 * top of that, `@radix-ui/react-dialog`'s non-modal `DialogContentNonModal`
 * ALWAYS calls `event.preventDefault()` in its own default handler once
 * the no-op trigger-focus attempt returns — which also suppresses
 * `FocusScope`'s own native "restore focus to whatever was focused when
 * the scope mounted" unmount fallback. Net effect without this hook:
 * closing the drawer silently leaves focus on nowhere in particular
 * (`<body>`), never back on the element that opened it.
 *
 * The actual opener capture lives in `useAdvisorDrawerStore.openDrawer`
 * itself (see `lastOpener`/`captureOpenerElement` in `stores/advisor.ts`)
 * — it has to happen there, synchronously, at the moment the drawer is
 * asked to open, before React ever re-renders; by the time ANY effect
 * reacting to `drawerOpen` becoming `true` could run, the drawer's own
 * `onOpenAutoFocus` handler (a child effect, which fires before this
 * hook's host component's effects would) has already stolen focus to the
 * composer textarea.
 *
 * Returns the `onCloseAutoFocus` handler to pass straight to
 * `DialogPrimitive.Content`, plus `skipNextRestore()` — call it before a
 * close that navigates away (Expand to the full page): focus belongs to
 * the destination route there, not back to whatever opened the drawer.
 */
export function useRestoreFocusOnClose(): {
  handleCloseAutoFocus: (event: Event) => void;
  skipNextRestore: () => void;
} {
  const skipRef = useRef(false);

  const skipNextRestore = useCallback(() => {
    skipRef.current = true;
  }, []);

  const handleCloseAutoFocus = useCallback((event: Event) => {
    if (skipRef.current) {
      skipRef.current = false;
      return;
    }
    const opener = useAdvisorDrawerStore.getState().lastOpener;
    if (opener && document.contains(opener)) {
      event.preventDefault();
      opener.focus();
      return;
    }
    // No capturable opener (nothing meaningful was focused when the
    // drawer opened — e.g. the `⌘/Ctrl+K` shortcut fired with nothing
    // focused — or the original opener has since unmounted while the
    // drawer was open): fall through to Radix's own default, which lands
    // focus on `<body>`. There is no single stable element to fall back
    // to across every entry surface (header pill, sidebar item, mobile
    // tab, contextual CTA all render different, page-specific markup).
  }, []);

  return { handleCloseAutoFocus, skipNextRestore };
}
